/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission irors hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import config from '../../../../share/config';
import session from '../../../../share/session';
import error from '../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  CONFIRMATION: 'confirmation',
  REGISTERED_FAILED: 'registeredFailed',
  UNREGISTERED_FAILED: 'unregisteredFailed',
  COMMUNICATION_FAILED: 'communicationFailed',
  MISMATCH_FAILED: 'mismatchFailed',
  FAILED: 'failed',
  SUCCESS: 'success'
};

export default {
  name: 'requirement051',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      airbagVin: '',
      ecuName: '',
      ecuVin: '',
      displayInfo: '',
      // ダイアログフラグ
      modal_requirement_051: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.REGISTERED_FAILED:
        case PHASE.UNREGISTERED_FAILED:
        case PHASE.COMMUNICATION_FAILED:
        case PHASE.MISMATCH_FAILED:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.CONFIRMATION:
        case PHASE.START:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_051 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // 初期表示画面の表示文言設定
      this.displayInfo = this.$t(this.i18nWsKey + '.REQ_WS_51_TXT_1');
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_051 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** OKボタン押下 */
    async exitProcess() {
      await this.exit();
    },

    /** データ読み出し */
    async read() {
      try {
        // スピナー開始
        this.isLoading = true;
        const token = session.getToken();

        // データ読み出し(要求)（バックエンドAPI_POST）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          const status = response.data.status;

          if (status === config.STATUS_WSP_COMPLETED) {
            // 正常終了：読み出しデータ表示
            const readData = response.data.user_data.value;
            if (readData === 'unregistered') {
              // 0x00(unregistered)の場合、登録へ
              await this.execute();
            } else if (readData === 'registered') {
              // 0xFF(registered)の場合、登録済みエラー画面へ
              this.displayInfo = this.$t(this.i18nWsKey + '.REQ_WS_51_TXT_4');
              this.phase = PHASE.REGISTERED_FAILED;
            } else {
              this.showUnexpectedError();
            }
          } else {
            this.showUnexpectedError();
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 実行処理 */
    async execute() {
      const token = session.getToken();

      // VIN登録、システム名称読み出し、データ書き込み、データ読み出し(要求)（バックエンドAPI_POST）
      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: 'execute'
        },
        // エラー発生のコールバックを実装する。
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );

      if (response) {
        const status = response.data.status;

        if (status === config.STATUS_WSP_COMPLETED) {
          // 正常終了：読み出しデータ表示
          this.airbagVin = response.data.user_data.udsVin;
          this.ecuName = response.data.user_data.ecuName;
          this.ecuVin = response.data.user_data.obdVin;
          if (this.airbagVin === this.ecuVin) {
            // VIN記録領域ロック
            this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_51_TXT_2`)
              .replace('$DID$', this.airbagVin)
              .replaceAll('$ECUNAME$', this.ecuName)
              .replace('$VIN$', this.ecuVin);
            this.phase = PHASE.CONFIRMATION;
          } else {
            // VIN不一致エラー
            this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_51_TXT_6`)
              .replace('$DID$', this.airbagVin)
              .replaceAll('$ECUNAME$', this.ecuName)
              .replace('$VIN$', this.ecuVin);
            this.phase = PHASE.MISMATCH_FAILED;
          }
        } else if (status === config.STATUS_WSP_FAILED) {
          switch (response.data.async_code) {
            // システム名称読み出しが異常終了、
            // VINの9桁目が"0"～"9"または"X"以外、個別の通信エラー
            case error.WSP_COMMUNICATION_ERROR:
              this.displayInfo = this.$t(this.i18nWsKey + '.REQ_WS_51_TXT_7');
              this.phase = PHASE.COMMUNICATION_FAILED;
              break;
            // VIN未登録エラー
            case error.WSP_UNREGISTERED_ERROR:
              this.ecuName = response.data.user_data;
              this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_51_TXT_5`)
                .replace('$ECUNAME$', this.ecuName);
              this.phase = PHASE.UNREGISTERED_FAILED;
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        } else {
          this.showUnexpectedError();
        }
      }
    },

    /** データ書き込み */
    async write() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        // NonDefaultSession遷移、セキュリティアクセス、データ書き込み(要求)（バックエンドAPI_POST）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'write'
          },
          // エラー発生のコールバックを実装する。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          const status = response.data.status;

          if (status === config.STATUS_WSP_COMPLETED) {
            this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_51_TXT_3`)
              .replace('$DID$', this.airbagVin)
              .replace('$ECUNAME$', this.ecuName)
              .replace('$VIN$', this.ecuVin);
            this.phase = PHASE.SUCCESS;
          } else {
            this.showUnexpectedError();
          }
        } else {
          this.showUnexpectedError();
        }
      } finally {
        // スピナー終了
        this.isLoading = false;
      }
    },

    /** 終了処理 */
    async exit() {
      try {
        this.isLoading = true;
        const token = session.getToken();

        // DefaultSession遷移(要求)（バックエンドAPI_POST）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装する。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          const status = response.data.status;

          if (status === config.STATUS_WSP_COMPLETED) {
            // Wizardを閉じる
            this.closeWizard();
          } else {
            this.showUnexpectedError();
          }
        }
      } finally {
        // スピナー終了
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      if (this.phase === PHASE.FAILED || this.phase === PHASE.COMMUNICATION_FAILED) {
        // Wizardを閉じる
        this.closeWizard();
        return;
      }
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },
  },

  async mounted() {
    this.init();
  },
};